import memoizeOne from 'memoize-one'
import { clamp, reduce, reduced } from 'ramda'

import {
  DESKTOP_ITEM_HEIGHT,
  FETCH_SCOPING_KEYS,
  INFINITE_LOADER_THRESHOLD,
  MAX_STEP_DAYS,
  MOBILE_ITEM_HEIGHT,
  NO_MORE_ITEMS,
} from '~/src/Journal/constants'
import createLogger from '~/src/Lib/Logging'
import {
  EMPTY_ARRAY,
  EMPTY_OBJECT,
  getDateTime,
  memoize,
} from '~/src/Lib/Utils'

import { getListRef, getOnItemsRendered } from './callbacks'

const logger = createLogger('Journal/utils')

export const getCalendarDays = memoizeOne(({
  calendarStartDate,
  calendarEndDate,
}) => calendarStartDate
  .until(calendarEndDate)
  .splitBy({ days: 1 })
  .map(({ start: day }) => day.startOf('day')))
export const getCalendarEnd = dateTime => dateTime.endOf('month').endOf('week').endOf('day')
export const getCalendarStart = dateTime => dateTime.startOf('month').startOf('week').startOf('day')

export const getTopItemIndex = memoize(({
  topItem,
  journal: { items },
}) => (
  topItem
    ? items?.findIndex?.(item => item.uuid === topItem?.uuid) ?? -1
    : -1
))

export const getTopByTimestamp = memoizeOne((items, selectedDate) => {
  if (!items?.length) return null
  const selected = selectedDate.toISO()
  const [selectedDay] = selected.split('T')
  let lastLTIndex = null
  let firstGTIndex = null
  const topForSelected = items.find((item, index) => {
    if (!item || !item.localTimestamp) return false
    const { localTimestamp } = item
    const match = localTimestamp.startsWith?.(selectedDay)
    if (!match && localTimestamp < selected) {
      lastLTIndex = index
    }
    if (!match && firstGTIndex === null && localTimestamp > selected) {
      firstGTIndex = index
    }
    return match
  })

  if (topForSelected) {
    return topForSelected
  }

  const { [lastLTIndex]: lastBefore } = items
  const { [firstGTIndex]: firstAfter } = items

  if (!lastBefore && !firstAfter) return null

  if (!lastBefore && firstAfter) {
    // If more than one day different, return null
    return Math.abs(getDateTime(firstAfter).diff(selectedDate).as('days')) > 1
      ? null
      : firstAfter
  }
  if (!firstAfter && lastBefore) {
    // If more than one day different, return null
    const lastBeforeDiff = getDateTime(lastBefore.localTimestamp).diff(selectedDate).as('days')
    return Math.abs(lastBeforeDiff) > 1
      ? null
      : lastBefore
  }

  const lbDiff = Math.abs(selectedDate.diff(getDateTime(lastBefore.localTimestamp)).as('milliseconds'))
  const faDiff = Math.abs(selectedDate.diff(getDateTime(firstAfter.localTimestamp)).as('milliseconds'))
  return lbDiff <= faDiff ? lastBefore : firstAfter
})

export const getItemCountForDay = memoize((day, { items }) => {
  const isoDate = day.toISODate()
  return (
    items?.filter?.(item => (
      item?.localTimestamp ? item.localTimestamp?.startsWith?.(isoDate) : false
    ))?.length ?? 0
  )
})

export const isEnd = memoizeOne((which, props) => {
  const { calendarStartDate, calendarEndDate, journal: { items } } = props
  const datetime = which === 'start' ? calendarStartDate : calendarEndDate
  const [first, second] = items ?? EMPTY_ARRAY
  const isNearEnd = (
    first && first.data === NO_MORE_ITEMS
      && (!second || getDateTime(second.timestamp) <= datetime)
  )
  const [penultimate, last] = items?.slice?.(-2) ?? EMPTY_ARRAY
  const isFarEnd = (
    last && last.data === NO_MORE_ITEMS
      && (!penultimate || datetime >= getDateTime(penultimate.timestamp))
  )

  return !!(isNearEnd || isFarEnd)
})

export const getItemsWithoutEnds = items => items.filter(item => item.data !== NO_MORE_ITEMS)

const itemKey = (index, data) => data[index]?.uuid
export const getListProps = memoizeOne(kwargs => {
  const {
    breakpoint,
    dispatch,
    height,
    journal: {
      after,
      items
    },
    innerRef,
    listRef,
    onItemsRendered,
    width,
  } = kwargs
  const ITEM_HEIGHT = breakpoint === 'xs' ? MOBILE_ITEM_HEIGHT : DESKTOP_ITEM_HEIGHT
  let topItemIndex = 0
  const itemCount = (items?.length ?? 0)
  if (itemCount && ((itemCount * ITEM_HEIGHT) > height)) {
    topItemIndex = getTopItemIndex(kwargs)
  }
  // If top item is the 'end of journal' placeholder item, scroll down one item
  if (topItemIndex === -1) {
    topItemIndex = 0
    const { [topItemIndex]: topItem } = items
    if (topItem.uuid?.startsWith('no_more_items_after')) {
      topItemIndex = 1
    }
  }

  const initialScrollOffset = topItemIndex * ITEM_HEIGHT

  return {
    height,
    initialScrollOffset,
    itemCount,
    itemData: items,
    itemKey,
    itemSize: ITEM_HEIGHT,
    onItemsRendered: getOnItemsRendered(after, dispatch, onItemsRendered),
    ref: getListRef(listRef, innerRef),
    width,
  }
})

export const getInfiniteLoaderProps = memoizeOne(props => {
  const { isItemLoaded, loadMoreItems, totalItemCount: itemCount } = props

  return {
    isItemLoaded,
    loadMoreItems,
    itemCount,
    threshold: INFINITE_LOADER_THRESHOLD
  }
})

const SCOPE_TRANSFORMS = {
  activeTypes: (types, data) => types.filter(type => data.systemAllowedTypes.has(type)),
}

export const prepareScope = data => FETCH_SCOPING_KEYS.reduce((scope, key) => (data[key] ? {
  ...scope,
  [key]: key in SCOPE_TRANSFORMS ? SCOPE_TRANSFORMS[key](data[key], data) : data[key]
} : scope), EMPTY_OBJECT)

const FILTERED = {
  activeTypes: (types, data) => types.length < data.allowedTypes.length,
}

export const isFiltered = (filters, state) => reduce((filtered, [key, value]) => {
  const keyIsFiltered = key in FILTERED ? FILTERED[key](value, state) : Boolean(value)
  if (keyIsFiltered) return reduced(keyIsFiltered)
  return keyIsFiltered
}, false, Object.entries(filters))

export const getStepDays = (filtered, deviceCount) => {
  const max = filtered ? MAX_STEP_DAYS : MAX_STEP_DAYS / 2

  return clamp(1, max, max - (Math.round(deviceCount / 2000) * max))
}

export const getUsers = ({
  currentFacility,
  memberships,
  users,
}) => Object.values(memberships)
  .filter(({ facility, user }) => facility === currentFacility?.id && users[user])
  .map(({ user }) => users[user])
